import React, { useRef, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { useKeyboardControls } from '../../application/hooks/useKeyboardControls';
import { CONFIG } from '../../index';

const MOVE_SPEED = 30;
const ROTATE_SPEED = 1.5;

export function CameraRig() {
  const controlsRef = useRef<any>(null);
  const keysRef = useKeyboardControls();
  const { camera } = useThree();
  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls) return;
    camera.position.set(CONFIG.camX, CONFIG.camY, CONFIG.camZ);
    controls.target.set(CONFIG.targetX, CONFIG.targetY, CONFIG.targetZ);
    camera.lookAt(CONFIG.targetX, CONFIG.targetY, CONFIG.targetZ);
    controls.update();
  }, []);

  useFrame((_, delta) => {
    const keys = keysRef.current;
    const controls = controlsRef.current;
    if (!controls) return;

    const speed = MOVE_SPEED * delta;
    const rotSpeed = ROTATE_SPEED * delta;

    // Get camera's forward direction projected onto ground plane (Z-up)
    const forward = new THREE.Vector3();
    camera.getWorldDirection(forward);
    forward.z = 0;
    forward.normalize();

    const right = new THREE.Vector3();
    right.crossVectors(forward, new THREE.Vector3(0, 0, 1)).normalize();

    const delta3 = new THREE.Vector3();

    // WASD — ground plane movement
    if (keys.forward) delta3.add(forward.clone().multiplyScalar(speed));
    if (keys.backward) delta3.add(forward.clone().multiplyScalar(-speed));
    if (keys.right) delta3.add(right.clone().multiplyScalar(speed));
    if (keys.left) delta3.add(right.clone().multiplyScalar(-speed));

    // Q/E — vertical
    if (keys.up) delta3.z += speed;
    if (keys.down) delta3.z -= speed;

    // Apply movement to both camera and target
    if (delta3.lengthSq() > 0) {
      camera.position.add(delta3);
      controls.target.add(delta3);
    }

    // Arrow keys — rotate around target
    if (keys.rotateLeft || keys.rotateRight || keys.rotateUp || keys.rotateDown) {
      const offset = camera.position.clone().sub(controls.target);
      const spherical = new THREE.Spherical().setFromVector3(offset);

      if (keys.rotateLeft) spherical.theta += rotSpeed;
      if (keys.rotateRight) spherical.theta -= rotSpeed;
      if (keys.rotateUp) spherical.phi = Math.max(0.1, spherical.phi - rotSpeed);
      if (keys.rotateDown) spherical.phi = Math.min(Math.PI - 0.1, spherical.phi + rotSpeed);

      offset.setFromSpherical(spherical);
      camera.position.copy(controls.target).add(offset);
    }

    controls.update();
  });

  return (
    <OrbitControls
      ref={controlsRef}
      target={[CONFIG.targetX, CONFIG.targetY, CONFIG.targetZ]}
      enableDamping
      dampingFactor={0.1}
      makeDefault
    />
  );
}
